import ParcelProvision from '../parcels/ParcelProvision';
import { useState, type FormEvent } from 'react';
import type { County, HomeownerProperty } from '../shared/api';
import { COUNTY_LABELS, COUNTY_OPTIONS } from '../shared/api';
import { api, ApiError } from '../lib/api';
import { fmtDate } from '../lib/format';
import { useSession } from '../lib/session';
import { useAsync } from '../lib/useAsync';
import { Banner, Button, Card, Empty, Field, Input, KeyValue, Modal, Pill, Select, Spinner } from '../components/ui';

/**
 * Homeowner home (Run 5, lienguardian.ai side). A homeowner tracks properties
 * in any Florida county. Where LienGuardian can read the county record it does
 * ("Is this correct?" → save, verified); where it cannot yet, the homeowner
 * enters it from the tax bill and it is saved as not verified. Public records:
 * no gate on watching.
 */

const FL_FIRST_STATES = ['FL', 'GA', 'NC', 'SC', 'TN', 'TX', 'LA'];

interface CountyRecord {
  parcelId: string;
  legalOwner: string;
  legalOwnerAddress: string | null;
  situsAddress: string | null;
  legalDescription: string | null;
  mortgage: { holderName: string } | null;
}
type LookupResult =
  | { status: 'found'; record: CountyRecord }
  | { status: 'multiple'; candidates: Array<{ parcelId: string; label: string }> }
  | { status: 'not_found'; message: string }
  | { status: 'error'; message: string };

type AddStep =
  | { step: 'form' }
  | { step: 'looking' }
  | { step: 'choose'; candidates: Array<{ parcelId: string; label: string }> }
  | { step: 'confirm'; record: CountyRecord }
  | { step: 'manual'; note: string | null };

type ListingPing = {
  parcelId: string;
  folio: string;
  address: string;
  agentEmail: string;
  activatePath: string;
};

export default function Properties() {
  const list = useAsync(() => api.get<{ properties: HomeownerProperty[] }>('/api/properties'), []);
  const pings = useAsync(() => api.get<{ pings: ListingPing[] }>('/api/me/listing-pings'), []);
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink">My Properties</h1>
          <p className="mt-0.5 text-sm text-muted">Track compliance on every property you own.</p>
        </div>
        <Button onClick={() => setAdding(true)}>Add My Property</Button>
      </div>

      {pings.data?.pings?.length ? (
        <Card title="Listing pings">
          <div className="space-y-3">
            {pings.data.pings.map((ping) => (
              <ListingPingRow key={ping.parcelId} ping={ping} onActivated={pings.reload} />
            ))}
          </div>
        </Card>
      ) : null}

      {list.loading ? (
        <Spinner label="Loading your properties…" />
      ) : list.error ? (
        <Banner tone="urgent" title="Could not load your properties">
          {list.error}
        </Banner>
      ) : !list.data?.properties.length ? (
        <Card>
          <Empty>
            No properties yet. Add your home and LienGuardian pulls the deed details from the county Property
            Appraiser automatically.
          </Empty>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {list.data.properties.map((p) => (
            <PropertyCard key={p.id} property={p} onRemoved={list.reload} />
          ))}
        </div>
      )}

      {adding && (
        <AddPropertyModal
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await list.reload();
          }}
        />
      )}
    </div>
  );
}

function ListingPingRow({ ping, onActivated }: { ping: ListingPing; onActivated: () => Promise<void> }) {
  const { contractor } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const personOk = !!contractor?.personVerifiedAt;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-ink">{ping.folio}</p>
        <p className="text-xs text-muted">{ping.address}</p>
        <p className="text-xs text-muted">
          {ping.agentEmail} listed this house.
          {personOk
            ? ' Activate to become the occupying owner.'
            : ' Speaking as seller requires a living person matching a government ID. Watching this folio does not.'}
        </p>
        {error && <p className="mt-1 text-xs text-muted">{error}</p>}
      </div>
      <Button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            if (!personOk) {
              const { url } = await api.post<{ url: string }>('/api/me/identity-session', {
                returnUrl: `${window.location.origin}/properties`,
              });
              window.location.assign(url);
              return;
            }
            await api.post(ping.activatePath, {});
            await onActivated();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not continue');
          } finally {
            setBusy(false);
          }
        }}
      >
        {personOk ? 'Activate listing' : busy ? 'Opening scan…' : 'Verify who you are'}
      </Button>
    </div>
  );
}

function PropertyCard({ property, onRemoved }: { property: HomeownerProperty; onRemoved: () => Promise<void> }) {
  const [removing, setRemoving] = useState(false);
  return (
    <Card
      title={`${property.address}, ${property.city}, ${property.state} ${property.zip}`}
      action={
        property.scrapedAt ? <Pill tone="none">No active contractor job</Pill> : <Pill tone="watch">Not verified</Pill>
      }
    >
      <KeyValue
        rows={[
          ['Owner of record', property.ownerName],
          ['Parcel ID', property.parcelId],
          ['Legal description', property.legalDescription],
          ['Mortgage holder', property.mortgageHolder ?? 'None on record'],
          [
            'County record',
            property.scrapedAt ? `Verified ${fmtDate(property.scrapedAt)}` : 'Not verified — entered by you',
          ],
        ]}
      />
      <p className="mt-4 text-sm text-muted">
        A watch is not a listing. If this house is for sale, the listing agent adds it on their office files and pings
        you — you do not start the sale from here.
      </p>
      <ParcelProvision source={{ ...property, kind: 'property' }} />
      <div className="mt-4 flex justify-end">
        <Button
          variant="ghost"
          disabled={removing}
          onClick={async () => {
            setRemoving(true);
            try {
              await api.del(`/api/properties/${property.id}`);
              await onRemoved();
            } finally {
              setRemoving(false);
            }
          }}
        >
          Remove
        </Button>
      </div>
    </Card>
  );
}

function AddPropertyModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const [state, setState] = useState<AddStep>({ step: 'form' });
  const [form, setForm] = useState({ address: '', city: '', state: 'FL', zip: '', county: '' as County | '', parcelId: '' });
  const [manual, setManual] = useState({ ownerName: '', parcelId: '', legalDescription: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const florida = form.state === 'FL';

  const lookup = async (by: { address?: string; parcelId?: string }) => {
    if (!form.county) return;
    setError(null);
    setState({ step: 'looking' });
    try {
      const { result } = await api.post<{ result: LookupResult }>('/api/properties/lookup', { county: form.county, ...by });
      if (result.status === 'found') setState({ step: 'confirm', record: result.record });
      else if (result.status === 'multiple') setState({ step: 'choose', candidates: result.candidates });
      else {
        // The server's reason is written for the team (adapter names,
        // certification levels); a homeowner gets the plain version.
        const county = COUNTY_LABELS[form.county];
        setManual((m) => ({ ...m, parcelId: m.parcelId || form.parcelId }));
        setState({
          step: 'manual',
          note:
            result.status === 'error'
              ? `LienGuardian can’t read ${county} County’s records automatically yet.`
              : `${county} County’s records didn’t show a match for that ${form.parcelId.trim() ? 'parcel number' : 'address'}.`,
        });
      }
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many lookups — wait a few seconds and try again.'
          : err instanceof Error
            ? err.message
            : 'Property lookup failed',
      );
      setState({ step: 'form' });
    }
  };

  const findProperty = (e: FormEvent) => {
    e.preventDefault();
    if (!florida || !form.county) {
      setState({ step: 'manual', note: null });
      return;
    }
    void lookup(form.parcelId.trim() ? { parcelId: form.parcelId.trim() } : { address: `${form.address}, ${form.city}` });
  };

  const save = async (fields: { ownerName: string; parcelId: string; legalDescription: string | null; mortgageHolder: string | null }) => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/api/properties', {
        address: form.address,
        city: form.city,
        state: form.state,
        zip: form.zip,
        county: form.county || null,
        ownerName: fields.ownerName || null,
        parcelId: fields.parcelId || null,
        legalDescription: fields.legalDescription || null,
        mortgageHolder: fields.mortgageHolder,
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this property');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add My Property" onClose={onClose}>
      {error && (
        <div className="mb-4">
          <Banner tone="urgent">{error}</Banner>
        </div>
      )}
      {state.step === 'looking' ? (
        <Spinner label="Finding your property in county records…" />
      ) : state.step === 'choose' ? (
        <div className="space-y-3">
          <p className="text-sm text-muted">The county has more than one match. Which is yours?</p>
          {state.candidates.map((c) => (
            <Button key={c.parcelId} variant="secondary" className="w-full justify-start" onClick={() => void lookup({ parcelId: c.parcelId })}>
              {c.label}
            </Button>
          ))}
          <Button variant="ghost" onClick={() => setState({ step: 'manual', note: null })}>
            None of these — enter it myself
          </Button>
        </div>
      ) : state.step === 'confirm' ? (
        <div className="space-y-4">
          <p className="text-sm text-muted">Here is what the county Property Appraiser has on file. Is this correct?</p>
          <KeyValue
            rows={[
              ['Address', `${form.address}, ${form.city}, ${form.state} ${form.zip}`],
              ['County', form.county ? COUNTY_LABELS[form.county] : null],
              ['Owner of record', state.record.legalOwner],
              ['Parcel ID', state.record.parcelId],
              ['Legal description', state.record.legalDescription],
              ['Mortgage holder', state.record.mortgage?.holderName ?? 'None on record'],
            ]}
          />
          <p className="text-xs text-dim">
            County records can lag a recent sale by weeks. If you sold or bought this property recently, the owner shown may not be
            current.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setState({ step: 'form' })}>
              Try again
            </Button>
            <Button
              disabled={saving}
              onClick={() =>
                void save({
                  ownerName: state.record.legalOwner,
                  parcelId: state.record.parcelId,
                  legalDescription: state.record.legalDescription,
                  mortgageHolder: state.record.mortgage?.holderName ?? null,
                })
              }
            >
              {saving ? 'Saving…' : 'Yes, track this property'}
            </Button>
          </div>
        </div>
      ) : state.step === 'manual' ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save({ ...manual, mortgageHolder: null });
          }}
        >
          <p className="text-sm text-muted">
            {state.note ? `${state.note} ` : ''}
            Enter what your tax bill or the county property appraiser’s website shows. It is saved as{' '}
            <strong>not verified</strong> until LienGuardian can read it from the county.
          </p>
          <Field label="Owner of record (as on the tax bill)">
            <Input value={manual.ownerName} onChange={(e) => setManual({ ...manual, ownerName: e.target.value })} />
          </Field>
          <Field label="Parcel / folio / STRAP number">
            <Input value={manual.parcelId} onChange={(e) => setManual({ ...manual, parcelId: e.target.value })} />
          </Field>
          <Field label="Legal description (optional)">
            <Input value={manual.legalDescription} onChange={(e) => setManual({ ...manual, legalDescription: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setState({ step: 'form' })}>
              Back
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Track this property'}
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={findProperty} className="space-y-4">
          <Field label="Street address">
            <Input
              required
              placeholder="123 Ocean View Dr"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City">
              <Input
                required
                placeholder="Cape Coral"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State">
                <Select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
                  {FL_FIRST_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="ZIP">
                <Input
                  required
                  placeholder="33904"
                  inputMode="numeric"
                  pattern="\d{5}(-\d{4})?"
                  value={form.zip}
                  onChange={(e) => setForm({ ...form, zip: e.target.value })}
                />
              </Field>
            </div>
          </div>
          {florida && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="County">
                <Select required value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value as County })}>
                  <option value="">Choose…</option>
                  {COUNTY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {COUNTY_LABELS[c]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Parcel number (optional)" hint="From your tax bill. Some counties, like Lee, look up by this only.">
                <Input value={form.parcelId} onChange={(e) => setForm({ ...form, parcelId: e.target.value })} />
              </Field>
            </div>
          )}
          <div className="flex justify-end">
            <Button type="submit">{florida ? 'Find My Property' : 'Continue'}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
